<template>
  <v-dialog v-model="dialog" width="1200" :persistent="true">
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>
          <v-layout row wrap align-end>
            <v-flex>
              {{ productName }}
              <span
                class="body-1"
                v-for="(productCategoryNames, key) in productCategoryName"
                :key="productCategoryNames"
              >
                {{ productCategoryNames }}
                <v-icon small v-if="key < productCategoryName.length - 1">chevron_right</v-icon>
              </span>
            </v-flex>
          </v-layout>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-layout row justify-end>
          <v-flex xs4>
            <v-text-field v-model="search" :label="'app.pages.products.nutrient' | trans"></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 d-flex>
            <v-data-iterator
              :items="[firstColumn]"
              :rows-per-page-items="[10000]"
              hide-actions
              content-tag="v-layout"
              row
              wrap
              justify-center
              fill-height
            >
              <template v-slot:item="props">
                <v-flex xs12>
                  <v-card height="100%">
                    <v-toolbar color="primary" dark>
                      <v-toolbar-title>{{ 'app.menu.basic' | trans }}</v-toolbar-title>
                    </v-toolbar>
                    <v-list dense>
                      <v-list-tile v-for="item in props.item" :key="item.id">
                        <v-list-tile-content :class="{ w60: true }">{{ item.name }}:</v-list-tile-content>
                        <v-list-tile-content class="align-end">
                          {{ getNutrientValue(item) }}
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </v-card>
                </v-flex>
              </template>
            </v-data-iterator>
          </v-flex>
          <v-flex xs3 d-flex class="pl-1">
            <v-data-iterator
              :items="[secondColumn]"
              :rows-per-page-items="[10000]"
              hide-actions
              content-tag="v-layout"
              row
              wrap
              justify-center
              fill-height
            >
              <template v-slot:item="props">
                <v-flex xs12>
                  <v-card height="100%">
                    <v-toolbar color="primary" dark>
                      <v-toolbar-title>{{ 'app.minerals' | trans }}</v-toolbar-title>
                    </v-toolbar>
                    <v-list dense>
                      <v-list-tile v-for="item in props.item" :key="item.id">
                        <v-list-tile-content class="w60">{{ item.name }}:</v-list-tile-content>
                        <v-list-tile-content class="align-end">
                          {{ getNutrientValue(item) }}
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </v-card>
                </v-flex>
              </template>
            </v-data-iterator>
          </v-flex>
          <v-flex xs3 d-flex class="pl-1">
            <v-data-iterator
              :items="[thirdColumn]"
              :rows-per-page-items="[10000]"
              hide-actions
              content-tag="v-layout"
              row
              wrap
              justify-center
              fill-height
            >
              <template v-slot:item="props">
                <v-flex xs12>
                  <v-card height="100%">
                    <v-toolbar color="primary" dark>
                      <v-toolbar-title>{{ 'app.vitamins' | trans }}</v-toolbar-title>
                    </v-toolbar>
                    <v-list dense>
                      <v-list-tile v-for="item in props.item" :key="item.id">
                        <v-list-tile-content :class="{ w60: true }">{{ item.name }}:</v-list-tile-content>
                        <v-list-tile-content class="align-end">
                          {{ getNutrientValue(item) }}
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </v-card>
                </v-flex>
              </template>
            </v-data-iterator>
          </v-flex>
          <v-flex xs3 d-flex class="pl-1">
            <v-data-iterator
              :items="[fourthColumn]"
              :rows-per-page-items="[10000]"
              hide-actions
              content-tag="v-layout"
              row
              wrap
              justify-center
              fill-height
            >
              <template v-slot:item="props">
                <v-flex xs12>
                  <v-card height="100%">
                    <v-toolbar color="primary" dark>
                      <v-toolbar-title>{{ 'app.amino_acids' | trans }}</v-toolbar-title>
                    </v-toolbar>
                    <v-list dense>
                      <v-list-tile v-for="item in props.item" :key="item.id">
                        <v-list-tile-content class="w60">{{ item.name }}:</v-list-tile-content>
                        <v-list-tile-content class="align-end">
                          {{ getNutrientValue(item) }}
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </v-card>
                </v-flex>
              </template>
            </v-data-iterator>
          </v-flex>
        </v-layout>
        <v-layout row pt-2>
          <v-flex xs12>
            <strong>{{ 'app.pages.products_weights' | trans }}:</strong>
            <v-chip color="green" text-color="white" v-for="weight in fifthColumn" :key="weight.id">
              {{ weight.name }}: <strong>{{ weight.value }}g</strong>
            </v-chip>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs12>
            <strong>{{ 'app.pages.products.seasonality' | trans }}:</strong>
            <v-chip color="green" text-color="white" v-for="month in sixthColumn" :key="month.name">
              {{ month.name }}
            </v-chip>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs12>
            <strong>{{ 'app.tags.tags' | trans }}:</strong>
            <span v-if="tags.length === 0">{{ 'app.no_data' | trans }}</span>
            <v-chip color="green" text-color="white" v-for="tag in tags" :key="tag.uuid">
              {{ tag.name }}
            </v-chip>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs12>
            <strong>{{ 'app.pages.products.type' | trans }}:</strong> {{ type }}
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import ProductInitialDataService from '../../services/ProductInitialDataService';
import { trans } from '../../app';
import { helpers } from '../../helpers';
import { glCalculation, wbtCalculation, wwCalculation } from '../../utils/additionalNutrientCalculators';

export default {
  data: function () {
    return {
      WW_ID: 153,
      WBT_ID: 154,
      CARBO_ID: 3,
      FIBER_ID: 20,
      PROTEIN_ID: 1,
      FATS_ID: 2,
      GLYCEMIC_INDEX_ID: 156,
      dialog: false,
      search: null,
      productName: null,
      productCategoryName: null,
      type: null,
      tags: [],
      columns: {
        firstColumn: [],
        secondColumn: [],
        thirdColumn: [],
        fourthColumn: [],
        fifthColumn: [],
        sixthColumn: [],
      },
      initialData: {
        firstColumn: [],
        secondColumn: [],
        thirdColumn: [],
        fourthColumn: [],
        fifthColumn: [],
        sixthColumn: [],
      },
    };
  },
  async created() {
    const initialDataService = new ProductInitialDataService();
    const { data } = await initialDataService.fetch();

    const nutrientMapper = (nutrient) => ({
      name: nutrient.name,
      id: nutrient.id,
      unit: nutrient.unit,
      short_name: nutrient.short_name,
      value: '',
    });
    this.columns.firstColumn = data[1].map(nutrientMapper);
    this.columns.secondColumn = data[2].map(nutrientMapper);
    this.columns.thirdColumn = data[3].map(nutrientMapper);
    this.columns.fourthColumn = data[4].map(nutrientMapper);
    this.columns.fifthColumn = Object.values(data[5]).map((weight) => ({
      name: weight.name,
      id: weight.id,
      value: '',
    }));

    this.initialData.firstColumn = helpers.deepClone(this.columns.firstColumn);
    this.initialData.secondColumn = helpers.deepClone(this.columns.secondColumn);
    this.initialData.thirdColumn = helpers.deepClone(this.columns.thirdColumn);
    this.initialData.fourthColumn = helpers.deepClone(this.columns.fourthColumn);
    this.initialData.fifthColumn = helpers.deepClone(this.columns.fifthColumn);
  },
  methods: {
    async open(productId) {
      this.dialog = true;
      const { data } = await axios.get(`/v2/products/${productId}`);
      const product = data.data;

      this.productName = product.name;
      this.productCategoryName = product.category.full_category;
      this.type = this.resolveType(product);
      this.tags = product.tags;

      const findNutrientAndSetValue = (nutrient) => {
        const nutrientWithValue = product.nutrients.find((nutrientWithValue) => nutrientWithValue.id === nutrient.id);
        if (nutrientWithValue !== undefined) {
          nutrient.value = nutrientWithValue.value;
        }
      };
      this.columns.firstColumn.forEach(findNutrientAndSetValue);
      this.columns.secondColumn.forEach(findNutrientAndSetValue);
      this.columns.thirdColumn.forEach(findNutrientAndSetValue);
      this.columns.fourthColumn.forEach(findNutrientAndSetValue);

      this.columns.fifthColumn.forEach((weight) => {
        const weightWithValue = product.weights.find((weightWithValue) => weightWithValue.id === weight.id);
        if (weightWithValue !== undefined) {
          weight.value = weightWithValue.weight;
        }
      });
      this.columns.sixthColumn = product.seasons.map((season) => ({ name: this.getMonths()[season] }));

      const carbo = product.nutrients.find((nutrient) => nutrient.id === this.CARBO_ID);
      const carboValue = carbo !== undefined ? carbo.value : 0;
      const fiber = product.nutrients.find((nutrient) => nutrient.id === this.FIBER_ID);
      const fiberValue = fiber !== undefined ? fiber.value : 0;
      const protein = product.nutrients.find((nutrient) => nutrient.id === this.PROTEIN_ID);
      const proteinValue = protein !== undefined ? protein.value : 0;
      const fats = product.nutrients.find((nutrient) => nutrient.id === this.FATS_ID);
      const fatsValue = fats !== undefined ? fats.value : 0;
      const gi = product.nutrients.find((nutrient) => nutrient.id === this.GLYCEMIC_INDEX_ID);
      const giValue = gi !== undefined ? gi.value : 0;

      this.columns.firstColumn.push(
        {
          name: trans('app.products.create_modal.lg'),
          id: this.GLYCEMIC_LOAD_ID,
          value: glCalculation(carboValue, fiberValue, giValue),
        },
        {
          name: trans('app.products.create_modal.ww'),
          id: this.WW_ID,
          value: wwCalculation(carboValue, fiberValue),
        },
        {
          name: trans('app.products.create_modal.wbt'),
          id: this.WBT_ID,
          value: wbtCalculation(proteinValue, fatsValue),
        }
      );
    },
    close() {
      this.dialog = false;
      this.search = null;
      this.productName = null;
      this.productCategoryName = null;
      this.columns.firstColumn = helpers.deepClone(this.initialData.firstColumn);
      this.columns.secondColumn = helpers.deepClone(this.initialData.secondColumn);
      this.columns.thirdColumn = helpers.deepClone(this.initialData.thirdColumn);
      this.columns.fourthColumn = helpers.deepClone(this.initialData.fourthColumn);
      this.columns.fifthColumn = helpers.deepClone(this.initialData.fifthColumn);
    },
    getMonths() {
      return {
        1: trans('app.months.january'),
        4: trans('app.months.april'),
        7: trans('app.months.july'),
        10: trans('app.months.october'),
        2: trans('app.months.february'),
        5: trans('app.months.may'),
        8: trans('app.months.august'),
        11: trans('app.months.november'),
        3: trans('app.months.march'),
        6: trans('app.months.june'),
        9: trans('app.months.september'),
        12: trans('app.months.december'),
      };
    },
    getNutrientValue(nutrient) {
      return nutrient.value ? `${nutrient.value}${nutrient.unit || ''}` : '-';
    },
    filterElements(el) {
      return el.name.toLowerCase().includes(this.search.toLowerCase());
    },
    resolveType(product) {
      if (product.is_shared) {
        return trans('app.products.create_modal.shared_product');
      }
      if (product.is_system) {
        return trans('app.products.create_modal.system_product');
      }

      return trans('app.products.create_modal.own_product');
    },
  },
  computed: {
    firstColumn: function () {
      if (!!this.search) {
        return this.columns.firstColumn.filter(this.filterElements);
      }

      return this.columns.firstColumn;
    },
    secondColumn: function () {
      if (!!this.search) {
        return this.columns.secondColumn.filter(this.filterElements);
      }

      return this.columns.secondColumn;
    },
    thirdColumn: function () {
      if (!!this.search) {
        return this.columns.thirdColumn.filter(this.filterElements);
      }

      return this.columns.thirdColumn;
    },
    fourthColumn: function () {
      if (!!this.search) {
        return this.columns.fourthColumn.filter(this.filterElements);
      }

      return this.columns.fourthColumn;
    },
    fifthColumn: function () {
      return this.columns.fifthColumn.filter((weight) => !!weight.value);
    },
    sixthColumn: function () {
      return this.columns.sixthColumn.length === 12
        ? [
            {
              name: trans('app.products.create_modal.whole_year'),
            },
          ]
        : this.columns.sixthColumn;
    },
  },
};
</script>

<style scoped>
.w60 {
  width: 60%;
}
>>> .v-list__tile:not(.v-list__tile--avatar) {
  height: 32px;
}

>>> .v-list__tile:hover {
  background: #eee;
}

.v-dialog__content {
  z-index: 1030 !important;
}
</style>
